import ReactModal from "react-modal";

import type { ReactNode } from "react";
import isBrowser from "~/kernel/browser/isBrowser";
import IconButton from "./IconButton";
import className from "~/kernel/styling/className";
import Heading from "./Heading";

function getAppElement() {
  if (isBrowser()) {
    return document.getElementById("root") ?? undefined;
  }

  return undefined;
}

type ModalProps = {
  title?: ReactNode;
  show: boolean;
  onCancel: () => unknown;
  children: ReactNode;
  size?: "auto" | "full";
  footer?: ReactNode;
};

const DEFAULT_SIZE = "auto";

export default function Modal(props: ModalProps) {
  const { children, title, show, onCancel, footer } = props;

  return (
    <ReactModal
      appElement={getAppElement()}
      isOpen={show}
      onRequestClose={onCancel}
      onAfterOpen={() => (document.body.style.overflow = "hidden")}
      onAfterClose={() => (document.body.style.overflow = "unset")}
      // react-modal applies afterOpen in a requestAnimationFrame callback.
      // A CSS animation on mount is not dependent on that frame, so the overlay
      // becomes visible even when the browser is starved for frames.
      overlayClassName={{
        base: "animate-fade-in bg-[rgba(0,0,0,0.3)] duration-[200ms] fixed inset-0 transition-all z-20 max-w-[100vw]",
        afterOpen: "",
        beforeClose: "!opacity-0",
      }}
      closeTimeoutMS={200}
      className={getModalClasses(props)}
    >
      <ModalHeader title={title} onClose={onCancel} />
      <ModalBody children={children} />
      {footer ?? <ModalFooter />}
    </ReactModal>
  );
}

type ModalFooterProps = {
  children?: ReactNode;
  sticky?: boolean;
};

export function ModalFooter({ children, sticky = false }: ModalFooterProps) {
  return (
    <div
      className={className("w-full bg-white py-3 px-3 md:px-8 md:pb-8", {
        "sticky bottom-0": sticky,
      })}
    >
      {children}
    </div>
  );
}

type ModalBodyProps = {
  children: ReactNode;
};

function ModalBody({ children }: ModalBodyProps) {
  return <div className="flex-1 px-3 md:px-8 overflow-auto">{children}</div>;
}

type ModalHeaderProps = {
  title?: ReactNode;
  onClose: () => void;
};

function ModalHeader({ title, onClose }: ModalHeaderProps) {
  return (
    <div className="sticky top-0 z-20 bg-white p-3">
      <div className="grid grid-cols-[3rem,1fr,3rem]">
        <div />
        <div className="flex flex-col justify-center justify-self-center">
          <Heading level={4}>{title}</Heading>
        </div>
        <div className="justify-self-end">
          <IconButton icon="xmark" onClick={() => onClose()} />
        </div>
      </div>
    </div>
  );
}

function getModalClasses({ size = DEFAULT_SIZE }: ModalProps) {
  const commonClasses = {
    base: "absolute overflow-auto rounded-md bg-white transition-all duration-[200ms] flex flex-col",
    afterOpen: "!opacity-100 transition-all",
    beforeClose: "opacity-0",
  };

  const phoneClasses = {
    base: "left-0 bottom-0 h-[95dvh] w-screen translate-y-5",
    afterOpen: "!translate-y-0",
    beforeClose: "!translate-y-5",
  };

  const desktopClasses = {
    base: className(
      "md:relative",
      {
        auto: "md:top-1/2 md:left-1/2 md:h-auto md:max-h-[85vh] md:max-w-3xl md:-translate-y-[calc(50%-1rem)] md:-translate-x-1/2",
        full: "md:inset-[1.5rem] md:h-[calc(100%_-_3rem)] md:w-[calc(100%_-_3rem)]",
      }[size],
    ),
    afterOpen: className({
      "md:!-translate-y-[50%]": size === "auto",
    }),
    beforeClose: className({
      "md:-translate-y-[calc(50%-1rem)]": size === "auto",
    }),
  };

  return {
    base: className(commonClasses.base, phoneClasses.base, desktopClasses.base),
    afterOpen: className(
      commonClasses.afterOpen,
      phoneClasses.afterOpen,
      desktopClasses.afterOpen,
    ),
    beforeClose: className(
      commonClasses.beforeClose,
      phoneClasses.beforeClose,
      desktopClasses.beforeClose,
    ),
  };
}
